import { useEffect, useRef, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";
import LiveChat from "@/components/LiveChat";
import PromoBanner from "@/components/PromoBanner";
import ExitIntentBar from "@/components/ExitIntentBar";
import { getAccountHome } from "@/utils/accountRoute";
import "@/dark-skin.css";

// Pages — lazy-loaded for route-based code splitting.
// Each import() becomes its own webpack chunk, keeping the initial bundle small
// and eliminating the 2s+ main-thread long tasks caused by parsing 3MB of JS upfront.
const DiagnosePage         = lazy(() => import("@/pages/DiagnosePage"));
const HomeV5               = lazy(() => import("@/pages/HomeV5"));
const ZitisiBoard          = lazy(() => import("@/pages/ZitisiBoard"));
const DashboardPage        = lazy(() => import("@/pages/DashboardPage"));
const GarageKitPage        = lazy(() => import("@/pages/GarageKitPage"));
const AdvertisePage        = lazy(() => import("@/pages/AdvertisePage"));
const RankingPolicyPage    = lazy(() => import("@/pages/RankingPolicyPage"));
const AuthCallback         = lazy(() => import("@/pages/AuthCallback"));
const AuthPage             = lazy(() => import("@/pages/AuthPage"));
const SEOPage              = lazy(() => import("@/pages/SEOLandingPage"));
const AdminPage            = lazy(() => import("@/pages/AdminPage"));
const DataQualityPage      = lazy(() => import("@/pages/DataQualityPage"));
const AdminHerePage        = lazy(() => import("@/pages/AdminHerePage"));
const OBDSearchPage        = lazy(() => import("@/pages/OBDSearchPage"));
const RemoteOBDPage        = lazy(() => import("@/pages/RemoteOBDPage"));
const DiagnosticsPage      = lazy(() => import("@/pages/DiagnosticsPage"));
const PriceEstimatorPage   = lazy(() => import("@/pages/PriceEstimatorPage"));
const DisclaimerPage       = lazy(() => import("@/pages/DisclaimerPage"));
const ConsultingPage       = lazy(() => import("@/pages/ConsultingPage"));
const AdvertiserDashboard  = lazy(() => import("@/pages/AdvertiserDashboard"));
const PricingPage          = lazy(() => import("@/pages/PricingPage"));
const AddGaragePage        = lazy(() => import("@/pages/AddGaragePage"));
const RegisterPage         = lazy(() => import("@/pages/RegisterPage"));
const UserDashboard        = lazy(() => import("@/pages/UserDashboard"));
const DriverOS             = lazy(() => import("@/pages/driver/DriverOS"));
const GarageOS             = lazy(() => import("@/pages/garage/GarageOS"));
const PrivacyPage          = lazy(() => import("@/pages/PrivacyPage"));
const TermsPage            = lazy(() => import("@/pages/TermsPage"));
const PublicServiceBookPage= lazy(() => import("@/pages/PublicServiceBookPage"));
const MyServiceBookPage    = lazy(() => import("@/pages/MyServiceBookPage"));
const GarageOwnerDashboard = lazy(() => import("@/pages/GarageOwnerDashboard"));
const CheckoutPage         = lazy(() => import("@/pages/CheckoutPage"));
const CheckoutSuccessPage  = lazy(() => import("@/pages/CheckoutSuccessPage"));
const SeoPage              = lazy(() => import("@/pages/SeoPage"));
const PricesPage           = lazy(() => import("@/pages/PricesPage"));
const EmergencyPage        = lazy(() => import("@/pages/EmergencyPage"));
const CostPage             = lazy(() => import("@/pages/CostPage"));
const TopListPage          = lazy(() => import("@/pages/TopListPage"));
const ComparePage          = lazy(() => import("@/pages/ComparePage"));
const FavoritesPage        = lazy(() => import("@/pages/FavoritesPage"));
const FindMeGaragePage     = lazy(() => import("@/pages/FindMeGaragePage"));
const JobBiddingPage       = lazy(() => import("@/pages/JobBiddingPage"));
const InstantBookingPage   = lazy(() => import("@/pages/InstantBookingPage"));
const ServiceHistoryPage   = lazy(() => import("@/pages/ServiceHistoryPage"));
const PartnerPage          = lazy(() => import("@/pages/PartnerPage"));
const GarageRegistrationPage = lazy(() => import("@/pages/GarageRegistrationPage"));
const RentACarPage         = lazy(() => import("@/pages/RentACarPage"));
const KataskeviSitePage    = lazy(() => import("@/pages/KataskeviSitePage"));
const DemoGarageSitePage   = lazy(() => import("@/pages/DemoGarageSitePage"));
const CustomerSitePage     = lazy(() => import("@/pages/CustomerSitePage"));
const ClaimLandingPage     = lazy(() => import("@/pages/ClaimLandingPage"));

// SEO Landing Pages
const SEOLandingPage       = lazy(() => import("@/pages/SEOLandingPage"));
const ServiceLandingPage   = lazy(() => import("@/pages/ServiceLandingPage"));
const BrandLandingPage     = lazy(() => import("@/pages/BrandLandingPage"));
const BrandLandingPageNew  = lazy(() => import("@/pages/redesign/BrandLandingPageNew"));
const FuelCostCalculator   = lazy(() => import("@/pages/redesign/FuelCostCalculatorPage"));
const EvChargingCost       = lazy(() => import("@/pages/redesign/EvChargingCostPage"));
const TotalCostOwnership   = lazy(() => import("@/pages/redesign/TotalCostOwnershipPage"));
const LeasingCalculator    = lazy(() => import("@/pages/redesign/LeasingCalculatorPage"));
const InsuranceCalculator  = lazy(() => import("@/pages/redesign/InsuranceCalculatorPage"));
const ParkingCalculator    = lazy(() => import("@/pages/redesign/ParkingCalculatorPage"));
const CarLoanCalculator    = lazy(() => import("@/pages/redesign/CarLoanCalculatorPage"));
const CalculatorsHub       = lazy(() => import("@/pages/redesign/CalculatorsHubPage"));
const CarValuation         = lazy(() => import("@/pages/redesign/CarValuationPage"));
const RoadTaxCalculator    = lazy(() => import("@/pages/redesign/RoadTaxCalculatorPage"));
const AllCitiesPage        = lazy(() => import("@/pages/AllCitiesPage"));
const AllServicesPage      = lazy(() => import("@/pages/AllServicesPage"));
const AllBrandsPage        = lazy(() => import("@/pages/AllBrandsPage"));
const ForProfessionalsPage = lazy(() => import("@/pages/ForProfessionalsPage"));
const OBDCodePage          = lazy(() => import("@/pages/OBDCodePage"));
const SymptomPage          = lazy(() => import("@/pages/SymptomPage"));
const CostServicePage      = lazy(() => import("@/pages/CostServicePage"));
// Wave 54: redesigned cost page (Wave 51 template, hand-curated 16-service DB)
const KostosServicePage    = lazy(() => import("@/pages/redesign/KostosServicePage"));
const ServiceCityPage      = lazy(() => import("@/pages/ServiceCityPage"));
// Wave 53A: new redesign-style page for /synergeia/{city}/{service} combos
const CityServicePage      = lazy(() => import("@/pages/redesign/CityServicePage"));
const CategoryPage         = lazy(() => import("@/pages/CategoryPage"));
const BrandGaragePage      = lazy(() => import("@/pages/BrandGaragePage"));
const GuidePage            = lazy(() => import("@/pages/GuidePage"));
const KteoPage             = lazy(() => import("@/pages/KteoPage"));

// Redesign (/new/*) pages
const SearchPageNew        = lazy(() => import("@/pages/redesign/SearchPageNew"));
const GarageDetailPageNew  = lazy(() => import("@/pages/redesign/GarageDetailPageNew"));
const BlogPageNew          = lazy(() => import("@/pages/redesign/BlogPageNew"));
const BlogPostPageNew      = lazy(() => import("@/pages/redesign/BlogPostPageNew"));
const PricingPageNew       = lazy(() => import("@/pages/redesign/PricingPageNew"));
const OffersPageNew        = lazy(() => import("@/pages/redesign/OffersPageNew"));
const OfferDetailPageNew   = lazy(() => import("@/pages/redesign/OfferDetailPageNew"));
const PartnerPageNew       = lazy(() => import("@/pages/redesign/PartnerPageNew"));
const ClaimGaragePageNew   = lazy(() => import("@/pages/redesign/ClaimGaragePageNew"));

// Components
import SOSButton from "@/components/SOSButton";
import Footer from "@/components/Footer";
import CompareBar from "@/components/CompareBar";
import FeedbackBanner from "@/components/FeedbackBanner";
import { WelcomeModal, HelpButton, TooltipProvider } from "@/components/Onboarding";
import ErrorBoundary from "@/components/ErrorBoundary";
// Wave 28: Google CMP (Consent Management Platform) replaces our custom
// cookie banner + AdSense consent gating. Google CMP handles TCF v2.2 +
// GA4 Consent Mode v2 signals automatically. Banner now shipped by AdSense
// itself (via the script in index.html) for EEA/UK/CH visitors.
// Imports kept commented for quick rollback if needed:
// import CookieBanner from "@/components/redesign/CookieBanner";
// import AdSenseLoader from "@/components/AdSenseLoader";
import { installGlobalErrorHandlers } from "@/utils/debugReporter";

// Context
import { CompareProvider } from "@/context/CompareContext";
import { FavoritesProvider } from "@/context/FavoritesContext";
import { LanguageProvider } from "@/context/LanguageContext";
import LanguageRouter from "@/components/LanguageRouter";

// API is always available - served by Vercel serverless functions at /api
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Auto-inject Bearer token into all /api/ requests so every fetch() call is authenticated,
// regardless of whether individual components use credentials:include or nothing.
(function patchFetch() {
  const _fetch = window.fetch;
  window.fetch = function(url, opts = {}) {
    const urlStr = typeof url === 'string' ? url : url?.url || '';
    if (urlStr.includes('/api/')) {
      const token = localStorage.getItem('vroum_access_token');
      if (token && !opts.headers?.['Authorization']) {
        opts = { ...opts, headers: { 'Authorization': `Bearer ${token}`, ...opts.headers } };
      }
    }
    return _fetch(url, opts);
  };
})();

// AppRouter handles session_id detection synchronously
function AppRouter() {
  const location = useLocation();

  // OAuth safety net: Supabase may land the #access_token on the Site URL ("/")
  // instead of /auth/callback when the callback URL isn't allow-listed. Pick it up
  // on ANY page so Google/email login establishes the session regardless of where
  // the provider redirected — otherwise the user appears logged-out → login loop.
  useEffect(() => {
    const hash = window.location.hash || '';
    if (!hash.includes('access_token=')) return;
    if (window.location.pathname === '/auth/callback') return; // AuthCallback handles it
    const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
    const accessToken = params.get('access_token');
    if (!accessToken) return;
    const refreshToken = params.get('refresh_token');
    localStorage.setItem('vroum_access_token', accessToken);
    if (refreshToken) localStorage.setItem('vroum_refresh_token', refreshToken);
    // Strip the token from the URL so it isn't re-processed or leaked.
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => {
        if (!u) return;
        localStorage.setItem('vroum_user', JSON.stringify(u));
        const stored = localStorage.getItem('vroum_auth_redirect');
        localStorage.removeItem('vroum_auth_redirect');
        window.location.href = stored || getAccountHome(u);
      })
      .catch(() => {});
  }, []);

  // Toggle redesign theme on <body> for redesign-served routes. The global
  // dark theme set on body in index.css would otherwise leak cream text +
  // dark gradient into the white-themed redesign pages.
  // After the prod swap, the redesign IS the default — covers prod paths
  // (/, /search, /garage/:id, /offers*, /pricing) plus the legacy /new/*,
  // /redesign/*, /en/new/* preview paths still in use.
  useEffect(() => {
    const p = location.pathname;
    const stripEn = p.replace(/^\/en(?=\/|$)/, '') || '/';
    // Pages served by the redesign components — they MUST flip body[data-redesign]
    // so the legacy index.css cream-text rules (h1/h2/h3 → var(--vroum-text)) don't
    // wash them out. Anything missing here renders with washed-out headings.
    const REDESIGN_EXACT = new Set([
      '/', '/search', '/offers', '/pricing',
      '/login', '/auth', '/blog', '/ypiresies', '/markes', '/synergeia',
      // Wave (May 10): /price-estimator now uses RedesignLayout
      '/price-estimator',
      // Calculator hub + all 8 calculator slugs (+ aliases)
      '/ergaleia', '/ypologistes', '/calculators',
      '/kostos-kausimou', '/ypologistis-kausimou',
      '/teli-kykloforias', '/teli-kykloforias-2026', '/ypologistis-telon',
      '/kostos-fortisis-ev', '/ypologistis-fortisis',
      '/kostos-aytokinitou-mina', '/ypologistis-kostos-aytokinitou',
      '/kostos-leasing-vs-agora', '/leasing-vs-agora', '/ypologistis-leasing',
      '/asfaleia-aytokinitou-times', '/asfalistra-aytokinitou', '/ypologistis-asfaleias',
      '/kostos-parking-athina', '/kostos-parking', '/ypologistis-parking',
      '/kostos-daneioy-aytokinitou', '/daneio-aytokinitou', '/ypologistis-daneioy',
      '/poso-aksizei-aytokinito',
      '/rent-a-car', '/enoikiasi-autokintou',
    ]);
    const REDESIGN_PREFIX = [
      '/garage/', '/offers/', '/markes/', '/blog/',
      // Wave 51: TopListPage now uses RedesignLayout
      '/top/', '/top', '/to-kalytero-synergeio/', '/kalytero-synergeio/',
      // Wave 52: OBD pages now use RedesignLayout
      '/obd/', '/obd',
      // Wave 53A: City × service pages now use RedesignLayout
      '/synergeia/', '/ypiresies/', '/kathgories/',
      // Wave 54: Cost pages now use RedesignLayout
      '/kostos/', '/poso-kostizei/',
    ];
    const isRedesignProd =
      REDESIGN_EXACT.has(stripEn) ||
      REDESIGN_PREFIX.some(p => stripEn.startsWith(p));
    const isRedesignPreview =
      p === '/new' || p.startsWith('/new/') ||
      p === '/en/new' || p.startsWith('/en/new/') ||
      p === '/redesign' || p.startsWith('/redesign/');
    if (isRedesignProd || isRedesignPreview) {
      document.body.dataset.redesign = 'true';
    } else {
      delete document.body.dataset.redesign;
    }

    // Unify the light CRA subpages with the dark homepage philosophy. dark-skin.css
    // theming applies wherever body[data-skin="dark"] is set. Homepage + the already-
    // dark pages (obd/symptoms/for-professionals) manage their own dark styling and
    // are deliberately excluded.
    const DARK_SKIN_EXACT = new Set(['/pricing', '/search', '/blog', '/offers', '/checkout', '/diagnosi', '/diafimisou', '/consulting', '/add-garage']);
    const DARK_SKIN_PREFIX = ['/blog/', '/offers/', '/checkout/'];
    const isDarkSkin = DARK_SKIN_EXACT.has(stripEn) || DARK_SKIN_PREFIX.some(p => stripEn.startsWith(p));
    if (isDarkSkin) document.body.dataset.skin = 'dark';
    else delete document.body.dataset.skin;
  }, [location.pathname]);

  // Track page views on every route change
  useEffect(() => {
    const path = location.pathname + (location.search || '');
    try {
      const body = JSON.stringify({ path, referrer: document.referrer || '' });
      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(`${API}/track/pageview`, blob);
      } else {
        fetch(`${API}/track/pageview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true,
        }).catch(() => {});
      }
    } catch (e) {
      // never throw from analytics
    }
  }, [location.pathname]);

  // CRITICAL: Detect Supabase auth redirects in URL fragment SYNCHRONOUSLY
  // Supabase sends: #access_token=...&type=signup|recovery|magiclink
  // Legacy custom flow sends: #session_id=...
  const hash = location.hash || '';
  if (
    hash.includes('access_token=') ||
    hash.includes('error_code=') ||
    hash.includes('session_id=')
  ) {
    return <AuthCallback />;
  }
  
  return (
    <LanguageRouter>
      <Suspense fallback={null}>
        <Routes>
          <Route path="/en/*" element={<LocalizedRoutes />} />
          <Route path="/*" element={<LocalizedRoutes />} />
        </Routes>
      </Suspense>
      <PromoBanner skipHome />
      <ExitIntentBar />
      <LiveChat />
    </LanguageRouter>
  );
}

function LocalizedRoutes() {
  return (
    <Routes>
      <Route index element={<HomeV5 />} />
      <Route path="zitisi" element={<ZitisiBoard />} />
      <Route path="zitisi/:id" element={<ZitisiBoard />} />
      <Route path="synergeio/:slug/kit" element={<GarageKitPage />} />
      <Route path="diafimisou" element={<AdvertisePage />} />
      <Route path="politiki-katataxis" element={<RankingPolicyPage />} />
      <Route path="ranking-policy" element={<Navigate to="/politiki-katataxis" replace />} />
      <Route path="diagnosi" element={<DiagnosePage />} />
      <Route path="diagnosis" element={<DiagnosePage />} />
      {/* Redesign parallel routes under /new/* */}
      <Route path="search" element={<SearchPageNew />} />
      <Route path="garage/:id" element={<GarageDetailPageNew />} />
      <Route path="claim" element={<GarageRegistrationPage />} />
      <Route path="claim/:garageId" element={<ClaimGaragePageNew />} />
      {/* ── FLAGSHIP CANONICAL (cutover live) ─────────────────────────────────
          /dashboard → Driver OS · /my-garage → Garage OS (ownership-gated loaders).
          Legacy PRESERVED for rollback: /dashboard-legacy + /my-garage/manage/:tab. */}
      <Route path="dashboard" element={<DriverOS />} />
      <Route path="my-garage" element={<GarageOS />} />
      <Route path="dashboard-legacy" element={<DashboardPage />} />
      {process.env.NODE_ENV === 'development' && (
        <Route path="dev/dashboard-preview" element={<DriverOS forceMock />} />
      )}
      {process.env.NODE_ENV === 'development' && (
        <Route path="dev/garage-dashboard-preview" element={<GarageOS forceMock />} />
      )}
      {/* Business-Profile management surfaces: reuse the legacy dashboard opened to a
          specific tab, so GarageOS never self-links to /my-garage. */}
      <Route path="my-garage/manage" element={<GarageOwnerDashboard />} />
      <Route path="my-garage/manage/:tab" element={<GarageOwnerDashboard />} />
      <Route path="admin" element={<AdminPage />} />
      <Route path="admin/data-quality" element={<DataQualityPage />} />
      <Route path="admin/here" element={<AdminHerePage />} />
      <Route path="auth/callback" element={<AuthCallback />} />
      <Route path="auth" element={<AuthPage />} />
      <Route path="login" element={<AuthPage />} />
      <Route path="obd" element={<OBDSearchPage />} />
      <Route path="obd/:code" element={<OBDCodePage />} />
      <Route path="remote-obd" element={<RemoteOBDPage />} />
      <Route path="diagnostics" element={<DiagnosticsPage />} />
      <Route path="symptoms/:symptom" element={<SymptomPage />} />
      <Route path="price-estimator" element={<PriceEstimatorPage />} />
      <Route path="disclaimer" element={<DisclaimerPage />} />
      <Route path="terms" element={<DisclaimerPage />} />
      <Route path="consulting" element={<ConsultingPage />} />
      <Route path="business" element={<ConsultingPage />} />
      <Route path="pricing" element={<PricingPageNew />} />
      <Route path="legacy-pricing" element={<Navigate to="/pricing" replace />} />
      <Route path="for-professionals" element={<ForProfessionalsPage />} />
      <Route path="checkout/success" element={<CheckoutSuccessPage />} />
      <Route path="checkout" element={<CheckoutPage />} />
      <Route path="checkout/:tier" element={<CheckoutPage />} />
      <Route path="offers" element={<OffersPageNew />} />
      <Route path="offers/:offerId" element={<OfferDetailPageNew />} />
      <Route path="add-garage" element={<AddGaragePage />} />
      <Route path="register" element={<Navigate to="/auth?mode=register" replace />} />
      <Route path="my-cars" element={<UserDashboard />} />
      <Route path="service-book" element={<UserDashboard />} />
      <Route path="my-service-book" element={<MyServiceBookPage />} />
      {/* Advertiser Dashboard */}
      <Route path="advertiser" element={<AdvertiserDashboard />} />
      <Route path="advertiser/dashboard" element={<AdvertiserDashboard />} />
      <Route path="advertiser/success" element={<AdvertiserDashboard />} />
      {/* Legal Pages */}
      <Route path="privacy" element={<PrivacyPage />} />
      <Route path="terms" element={<TermsPage />} />
      {/* Price Catalog */}
      <Route path="times" element={<PricesPage />} />
      <Route path="times/:slug" element={<PricesPage />} />
      {/* Dynamic Cost Pages - SEO long-tail */}
      {/* Wave 54: programmatic "Πόσο κοστίζει..." pages now use the
          redesigned KostosServicePage with Wave 51 template + FAQPage
          + Offer JSON-LD. Greek-natural alias /poso-kostizei mirrors
          the literal search query. /kostos/{service}/{brand}* keeps
          legacy CostPage for now (brand-specific pricing is a Wave 55+
          extension). */}
      <Route path="kostos/:serviceSlug" element={<KostosServicePage />} />
      <Route path="poso-kostizei/:serviceSlug" element={<KostosServicePage />} />
      <Route path="kostos/:serviceSlug/:brandSlug" element={<CostPage />} />
      <Route path="kostos/:serviceSlug/:brandSlug/:modelSlug" element={<CostPage />} />
      {/* Top Lists SEO Pages */}
      <Route path="top" element={<TopListPage />} />
      <Route path="top/:brand" element={<TopListPage />} />
      <Route path="top/:brand/:city" element={<TopListPage />} />
      {/* Wave 51: Greek-natural URL aliases for "καλύτερο συνεργείο [brand] [city]"
          search pattern. Same component, same data, just slug shapes Google
          understands as exact-match. Canonical stays /top/{brand}/{city}. */}
      <Route path="to-kalytero-synergeio/:brand" element={<TopListPage />} />
      <Route path="to-kalytero-synergeio/:brand/:city" element={<TopListPage />} />
      <Route path="kalytero-synergeio/:brand" element={<TopListPage />} />
      <Route path="kalytero-synergeio/:brand/:city" element={<TopListPage />} />
      {/* Emergency / Panic Button */}
      <Route path="voitheia" element={<EmergencyPage />} />
      <Route path="emergency" element={<EmergencyPage />} />
      <Route path="sos" element={<EmergencyPage />} />
      {/* Compare Page */}
      <Route path="compare" element={<ComparePage />} />
      {/* Favorites Page */}
      <Route path="favorites" element={<FavoritesPage />} />
      {/* Blog Pages - SEO */}
      <Route path="blog" element={<BlogPageNew />} />
      <Route path="blog/:slug" element={<BlogPostPageNew />} />
      {/* Find Me a Garage - Lead Generation */}
      <Route path="vres-mou-synergeio" element={<FindMeGaragePage />} />
      <Route path="find-garage" element={<FindMeGaragePage />} />
      {/* Job Bidding System */}
      <Route path="apaitisi-prosforas" element={<JobBiddingPage />} />
      <Route path="jobs" element={<JobBiddingPage />} />
      {/* Instant Booking System - Uber style */}
      <Route path="booking" element={<InstantBookingPage />} />
      <Route path="booking/success" element={<InstantBookingPage />} />
      <Route path="booking/cancel" element={<InstantBookingPage />} />
      {/* Service History */}
      <Route path="service-history" element={<ServiceHistoryPage />} />
      {/* Why Claim Your Garage */}
      <Route path="claim-landing" element={<ClaimLandingPage />} />
      <Route path="giati-na-diektimis" element={<ClaimLandingPage />} />
      {/* Garage Website Builder — €250+ landing + demos */}
      <Route path="kataskevi-istoselidas-synergeia" element={<KataskeviSitePage />} />
      <Route path="website-synergeio"               element={<KataskeviSitePage />} />
      <Route path="demos/synergeio/:slug"           element={<DemoGarageSitePage />} />
      <Route path="sites/:slug"                     element={<CustomerSitePage />} />
      {/* Rent a Car */}
      <Route path="rent-a-car" element={<RentACarPage />} />
      <Route path="enoikiasi-autokintou" element={<RentACarPage />} />
      {/* Partner/Garage Onboarding */}
      <Route path="partner" element={<PartnerPage />} />
      <Route path="synergasia" element={<PartnerPage />} />
      {/* Simplified Garage Registration - Lead Magnet */}
      <Route path="register-garage" element={<GarageRegistrationPage />} />
      <Route path="eggrafi-synergeiou" element={<GarageRegistrationPage />} />
      {/* Public Service Book */}
      <Route path="car/:plate" element={<PublicServiceBookPage />} />
      {/* SEO Landing Pages - New */}
      <Route path="synergeia" element={<AllCitiesPage />} />
      {/* Wave 53C: Cyprus expansion — Cyprus city URLs now serve real
          /synergeia/{city}/{service} pages via CityServicePage. Removed
          the kill-redirects to /home; added the cities to SEO_CITIES
          so the route handler resolves them. Initial inventory is empty
          (no Cypriot garages scraped yet) but the URLs are crawlable so
          we capture branded "συνεργείο Λεμεσό" type queries while data
          fills in. */}
      {/* Wave 53A: new redesign-style City × Service page (replaces SEOLandingPage
          for these patterns). Uses Wave 51 template + FAQPage schema. /ypiresies/
          variant just redirects to canonical /synergeia/ inside the component. */}
      <Route path="synergeia/:city" element={<CityServicePage />} />
      <Route path="synergeia/:city/:serviceOrBrand" element={<CityServicePage />} />
      <Route path="ypiresies" element={<AllServicesPage />} />
      <Route path="ypiresies/:slug" element={<ServiceLandingPage />} />
      <Route path="ypiresies/:serviceSlug/:citySlug" element={<CityServicePage swapOrder />} />
      <Route path="markes" element={<AllBrandsPage />} />
      <Route path="markes/:slug" element={<BrandLandingPage />} />
      {/* SKYNET 2026 — redesigned brand garage pages with FAQ + ItemList JSON-LD.
          Targets the /markes/<brand>/synergeia URL pattern that already ranks
          high on "καλύτερο συνεργείο BMW" type queries (7-17% CTR per GSC).
          Falls back to legacy BrandGaragePage at /legacy-markes/* for rollback. */}
      <Route path="markes/:brand/synergeia" element={<BrandLandingPageNew />} />
      {/* SKYNET 2026 — Fuel cost calculator. High-engagement evergreen tool;
          targets "πόσο κοστίζει το αυτοκίνητο μου", "diesel vs βενζίνη" etc. */}
      <Route path="kostos-kausimou" element={<FuelCostCalculator />} />
      <Route path="ypologistis-kausimou" element={<FuelCostCalculator />} />
      {/* SKYNET 2026 — EV charging cost calculator. Targets fast-growing
          "ηλεκτρικό αυτοκίνητο φόρτιση κόστος" query cluster as Greece's
          EV adoption climbs (still <2 % share but +180 % YoY). */}
      <Route path="kostos-fortisis-ev" element={<EvChargingCost />} />
      <Route path="ypologistis-fortisis" element={<EvChargingCost />} />
      {/* SKYNET 2026 — Total Cost of Ownership: rolls up all 6 cost
          centers (fuel + tax + insurance + service + KTEO + depreciation)
          in one calculator. Targets "πόσο κοστίζει το αυτοκίνητο τον μήνα". */}
      <Route path="kostos-aytokinitou-mina" element={<TotalCostOwnership />} />
      <Route path="ypologistis-kostos-aytokinitou" element={<TotalCostOwnership />} />
      {/* SKYNET 2026 — Leasing vs buying calculator. Targets fast-growing
          query "leasing αυτοκινητου" (+45% YoY). Includes Greek-specific
          tax-deductibility for self-employed. */}
      <Route path="kostos-leasing-vs-agora" element={<LeasingCalculator />} />
      <Route path="leasing-vs-agora" element={<LeasingCalculator />} />
      <Route path="ypologistis-leasing" element={<LeasingCalculator />} />
      {/* SKYNET 2026 — Insurance estimator. Greek market estimator with
          coverage tiers, region risk, age penalty, bonus-malus discount. */}
      <Route path="asfaleia-aytokinitou-times" element={<InsuranceCalculator />} />
      <Route path="asfalistra-aytokinitou" element={<InsuranceCalculator />} />
      <Route path="ypologistis-asfaleias" element={<InsuranceCalculator />} />
      {/* SKYNET 2026 — Parking calculator. 9 Greek locations, 4 scenarios. */}
      <Route path="kostos-parking-athina" element={<ParkingCalculator />} />
      <Route path="kostos-parking" element={<ParkingCalculator />} />
      <Route path="ypologistis-parking" element={<ParkingCalculator />} />
      {/* SKYNET 2026 — Car loan calculator with French amortization. */}
      <Route path="kostos-daneioy-aytokinitou" element={<CarLoanCalculator />} />
      <Route path="daneio-aytokinitou" element={<CarLoanCalculator />} />
      <Route path="ypologistis-daneioy" element={<CarLoanCalculator />} />
      {/* SKYNET 2026 — Calculators hub. Single landing for all 8 cost
          calculators, ranks for "εργαλεία αυτοκινήτων". */}
      <Route path="ergaleia" element={<CalculatorsHub />} />
      <Route path="ypologistes" element={<CalculatorsHub />} />
      <Route path="calculators" element={<CalculatorsHub />} />
      {/* SKYNET 2026 — Used Car Valuator. Greek market depreciation curve. */}
      <Route path="poso-aksizei-aytokinito" element={<CarValuation />} />
      <Route path="aksia-aytokinitou" element={<CarValuation />} />
      <Route path="apotimisi-aytokinitou" element={<CarValuation />} />
      <Route path="teli-kykloforias" element={<RoadTaxCalculator />} />
      <Route path="teli-kykloforias-2026" element={<RoadTaxCalculator />} />
      <Route path="ypologistis-telon" element={<RoadTaxCalculator />} />
      <Route path="legacy-markes/:brandSlug/synergeia" element={<BrandGaragePage />} />
      {/* NEW: Category pages - /kathgories/{category} */}
      <Route path="kathgories/:categorySlug" element={<CategoryPage />} />
      {/* NEW: Guide pages - /odigoi/{slug} */}
      <Route path="odigoi/:guideSlug" element={<GuidePage />} />
      {/* NEW: KTEO content cluster - /kteo and /kteo/{city} (GSC pos 2.4 for "κτεο τιμη 2026") */}
      <Route path="kteo" element={<KteoPage />} />
      <Route path="kteo/timi" element={<KteoPage />} />
      <Route path="kteo/:city" element={<KteoPage />} />
      {/* SEO Pages - Dynamic */}
      <Route path="s/:type/:slug" element={<SEOPage />} />
      {/* Catch-all for SEO pages - must be last */}
      <Route path=":seoSlug" element={<SeoPage />} />
    </Routes>
  );
}

// Shell for all user-facing floating components (modals, banners, buttons).
// Hidden on admin routes so these never overlay the admin panel.
function PublicOnlyShell() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin') || location.pathname.startsWith('/en/admin');
  // Redesign-served routes ship their own RedesignLayout (Header + Footer);
  // skipping the production Footer + onboarding widgets here avoids visual
  // collisions (double footer, dark widgets on white pages). After the prod
  // swap this covers /, /search, /garage/:id, /offers*, /pricing plus the
  // legacy /new/* preview paths.
  const p = location.pathname;
  const stripEn = p.replace(/^\/en(?=\/|$)/, '') || '/';
  const isRedesignProd =
    stripEn === '/' ||
    stripEn === '/search' ||
    stripEn.startsWith('/garage/') ||
    stripEn === '/offers' || stripEn.startsWith('/offers/') ||
    stripEn === '/pricing' ||
    // Apple-skinned conversion flow — ships its own clean chrome, so skip the
    // legacy production Footer + dark floating widgets (old-skin collision).
    stripEn === '/eggrafi-synergeiou' ||
    stripEn === '/add-garage' ||
    stripEn === '/auth' || stripEn === '/login' ||
    stripEn === '/claim' || stripEn.startsWith('/claim/') ||
    // Apple-skinned owner dashboards — own clean chrome, skip legacy footer/widgets.
    stripEn === '/my-garage' || stripEn === '/dashboard';
  const isRedesignPreview = p.startsWith('/new') || p.startsWith('/en/new');
  if (isAdmin || isRedesignProd || isRedesignPreview) return null;
  return (
    <>
      <CompareBar />
      <Footer />
      {/* SOSButton removed per owner request — no bottom-left help/emergency FAB */}
      <FeedbackBanner />
      <WelcomeModal />
      {/* HelpButton (bottom-left "?" FAB) removed per owner request */}
    </>
  );
}

function App() {
  // Install global error handlers for debug reporting
  useEffect(() => {
    installGlobalErrorHandlers();
  }, []);

  // Seed data on first load - only if backend is configured
  useEffect(() => {
    if (!API) return;
    const seedData = async () => {
      try {
        await fetch(`${API}/seed`, { method: 'POST' });
      } catch (e) {
        console.log('Seed skipped (no backend):', e);
      }
    };
    seedData();
  }, []);

  return (
    <HelmetProvider>
      <LanguageProvider>
      <TooltipProvider>
        <CompareProvider>
          <FavoritesProvider>
            <div className="min-h-screen flex flex-col">
              <BrowserRouter>
                <ErrorBoundary>
                <div className="flex-1 pb-16">
                  <AppRouter />
                </div>
                <PublicOnlyShell />
                {/* Cookie banner now provided by Google CMP (Wave 28) */}
                {/* Newsletter exit-intent/scroll popup DISABLED 2026-06 — UX
                    audit: 28d data was 863 opens, 379 dismiss (43%), 0
                    subscribe. A "newsletter" has zero value for one-time
                    driver visitors; the popup only annoyed people. The opt-in
                    footer signup (Footer.jsx) stays for the rare interested
                    user. Re-enable only with a real incentive (e.g. price
                    alerts) and a measured offer. <NewsletterCapture /> */}
                </ErrorBoundary>
              </BrowserRouter>
              <Toaster position="bottom-right" />
            </div>
          </FavoritesProvider>
        </CompareProvider>
      </TooltipProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
